// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@guestsupply/App/Components/Notify/Notify";
import CartLineQuantity, { CartLineQuantityStyles } from "@guestsupply/Overrides/Widgets/Cart/CartLineQuantity";
import Close from "@guestsupply/XDL/Icons/Close";
import { color, ellipsis, hideInputNumberArrows } from "@guestsupply/XDL/Utilities";
import { xScreen } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    hideTippy: () => void;
    cart?: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
}

export interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    imageWrapper?: InjectableCss;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    imageAndDescriptionWrapper?: InjectableCss;
    leftSideWrapper?: InjectableCss;
    rightSideWrapper?: InjectableCss;
    lineDescription?: LinkPresentationProps;
    qtyWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    extendedPriceWrapper?: InjectableCss;
    basicProductPriceStyles?: ProductPriceStyles;
    extendedNetPriceValue?: TypographyProps;
    totalPriceWrapper?: InjectableCss;
    cartLineTotal?: TypographyProps;
    removeCartLineLink?: LinkPresentationProps;
    errorMessagesWrapper?: InjectableCss;
    seperator?: InjectableCss;
    notify?: TypographyProps;
}

export const miniCartLineStyles: MiniCartLineStyles = {};

const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLine = ({
    cartLine,
    errorMessages,
    hideTippy,
    cart,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
}: Props) => {
    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                <StyledWrapper {...styles.detailsWrapper}>
                    <StyledWrapper {...styles.contentWrapper}>
                        <StyledWrapper {...styles.imageAndDescriptionWrapper}>
                            <StyledWrapper {...styles.imageWrapper}>
                                <Link
                                    href={cartLine.productUri}
                                    onClick={hideTippy}
                                    aria-label={`${cartLine.altText} link`}
                                >
                                    <LazyImage
                                        width="60"
                                        height="60"
                                        src={cartLine.smallImagePath}
                                        altText={cartLine.altText}
                                        {...styles.cartLineImage}
                                    />
                                </Link>
                            </StyledWrapper>
                            <Link
                                href={cartLine.productUri}
                                onClick={hideTippy}
                                {...styles.lineDescription}
                                aria-label={`${cartLine.shortDescription} Link`}
                            >
                                {cartLine.shortDescription.length > 60
                                    ? `${cartLine.shortDescription.slice(0, 60)}...`
                                    : cartLine.shortDescription}
                            </Link>
                        </StyledWrapper>

                        <StyledWrapper {...styles.qtyWrapper}>
                            <CartLineQuantity
                                cart={cart as Cart}
                                editable={true}
                                extendedStyles={styles.cartLineQuantityStyles}
                                onQtyOrderedChange={(inputQty: number) => {
                                    qtyOrderedChangeHandler(cartLine, inputQty);
                                }}
                                showActions={false}
                                showMultipleSaleQty={false}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.extendedPriceWrapper}>
                            <ProductPrice
                                showLabel={false}
                                product={cartLine}
                                extendedStyles={styles.basicProductPriceStyles}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.totalPriceWrapper}>
                            {!cart?.cartNotPriced &&
                            cartLine &&
                            cartLine.pricing &&
                            cartLine.pricing.extendedUnitNetPrice > 0 ? (
                                <Typography {...styles.cartLineTotal}>
                                    {cartLine.pricing.extendedUnitNetPriceDisplay}
                                </Typography>
                            ) : null}
                        </StyledWrapper>
                        <StyledWrapper>
                            <Link
                                onClick={() => removeCartLineClickHandler(cartLine)}
                                {...styles.removeCartLineLink}
                                aria-label="Remove Cart Line"
                            >
                                <Icon src={Close} />
                            </Link>
                        </StyledWrapper>
                    </StyledWrapper>

                    {errorMessages && (
                        <StyledWrapper {...styles.errorMessagesWrapper}>
                            {errorMessages.map(message => (
                                <Notify
                                    {...styles.notify}
                                    type="info"
                                    data-test-selector="cartline_errorMessage"
                                    key={`error_${cartLine.id}`}
                                >
                                    {message}
                                </Notify>
                            ))}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.seperator}></StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLine;
